import { useCallback } from "react";

import { type RecordLearningWalkthroughOutcomeMutationVariables } from "~/gql/mutations/learning.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useRecordLearningConceptExplainedMutation,
	useRecordLearningWalkthroughOutcomeMutation
} from "~/services/endpoints/apiEndpoints";

export interface UseLearningMutationsReturn {
	recordConceptExplained: (conceptId: string) => Promise<boolean>;
	recordWalkthroughOutcome: (
		variables: RecordLearningWalkthroughOutcomeMutationVariables,
		successMessage?: string
	) => Promise<boolean>;
}

const ENTITY_LABEL = "Walkthrough progress";

const CONCEPT_ENTITY_LABEL = "Learning progress";

const useLearningMutations = (): UseLearningMutationsReturn => {
	const { run } = useMutationRunner();
	const [recordExplainedMutation] = useRecordLearningConceptExplainedMutation();
	const [recordOutcomeMutation] = useRecordLearningWalkthroughOutcomeMutation();

	const recordConceptExplained = useCallback(
		(conceptId: string): Promise<boolean> =>
			run(
				recordExplainedMutation({ conceptId }),
				(data) => data.recordLearningConceptExplained,
				snackMessages.saveError(CONCEPT_ENTITY_LABEL)
			),
		[recordExplainedMutation, run]
	);

	const recordWalkthroughOutcome = useCallback(
		(variables: RecordLearningWalkthroughOutcomeMutationVariables, successMessage?: string): Promise<boolean> =>
			run(
				recordOutcomeMutation(variables),
				(data) => data.recordLearningWalkthroughOutcome,
				snackMessages.saveError(ENTITY_LABEL),
				{ silent: true, successMessage }
			),
		[recordOutcomeMutation, run]
	);

	return { recordConceptExplained, recordWalkthroughOutcome };
};

export default useLearningMutations;
